import type { AgentExecutionStatus } from "@/types/agent-execution";

export enum AnalyticsResultType {
  TABLE = "TABLE",
  MESSAGE = "MESSAGE",
  QUESTION = "QUESTION",
}

/** Inline artifact stored alongside assistant messages */
export interface ChatArtifact {
  columns: string[];
  rows: unknown[][];
  sql?: string | null;
  explanation?: string;
  chart_type?: string;
  currency_column_indices?: number[];
  /** Backend-computed type per column: 'currency' | 'numeric' | 'time' | 'category' | 'text' */
  column_types?: Record<string, string>;
  python_code?: string | null;
  /** Chart configuration from backend (Mode 2 format) */
  chartConfig?: ChatArtifactChartConfig;
}

/** Chart config shape returned by the backend */
/** Shared dashboard chart configuration; its wire vocabulary is not Ask-owned. */
export interface ChatArtifactChartConfig {
  chartType: "line" | "bar" | "donut" | "metric" | "table" | "scatter";
  xColumn?: string;
  yColumns?: string[];
  xLabel?: string;
  yLabel?: string;
}

export interface AnalyticsHistoryMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AnalyticsFileAttachment {
  name: string;
  type: string;
  data: string; // base64
}

export interface AskAnalyticsRequest {
  question: string;
  tz?: string;
  history?: AnalyticsHistoryMessage[];
  files?: AnalyticsFileAttachment[];
  /** Last analytics result from previous turn. Enables "plot this" follow-ups. */
  last_result?: AnalyticsResult;
}

export interface AnalyticsStep {
  step: string;
  status: AgentExecutionStatus;
  title?: string;
  elapsed_ms: number;
  sql?: string;
  python_code?: string;
  explanation?: string;
  message?: string;
  reason?: string;
  result?: AnalyticsResult;
}

/** Agent-selected visualization type, matching backend AskDecodaChartType. */
export enum AnalyticsChartType {
  TABLE = "TABLE",
  BAR = "BAR",
  LINE = "LINE",
  AREA = "AREA",
  PIE = "PIE",
  METRIC = "METRIC",
  SCATTER = "SCATTER",
  COMPOSED = "COMPOSED",
  BIAXIAL = "BIAXIAL",
  SANKEY = "SANKEY",
  STACKED_SIGN = "STACKED_SIGN",
  FUNNEL = "FUNNEL",
  TREEMAP = "TREEMAP",
  BOX_PLOT = "BOX_PLOT",
}

export interface AnalyticsPresentationFields {
  chart_title?: string;
  x_label?: string;
  y_label?: string;
  x_column?: string;
  y_columns?: string[];
  currency_columns?: string[];
}

/** A single data artifact (chart, table, etc.) within a multi-artifact response */
export interface AnalyticsArtifact extends AnalyticsPresentationFields {
  execution_time_ms?: number;
  columns: string[];
  rows: unknown[][];
  sql?: string | null;
  explanation?: string;
  chart_type?: AnalyticsChartType;
  python_code?: string | null;
  currency_column_indices?: number[];
  /** Backend-computed type per column: 'currency' | 'numeric' | 'time' | 'category' | 'text' */
  column_types?: Record<string, string>;
}

export interface AnalyticsResult {
  explanation: string;
  /** LLM-generated narrative shown above the table (when present) */
  narrative?: string;
  result_type: AnalyticsResultType;
  columns: string[];
  rows: unknown[][];
  message: string | null;
  sql: string | null;
  execution_time_ms: number;
  /** Backend-computed type per column: 'currency' | 'numeric' | 'time' | 'category' | 'text' */
  column_types?: Record<string, string>;
  /** @deprecated Use column_types instead */
  currency_column_indices?: number[];
  /** Python code used for analysis (when needs_python) */
  python_code?: string | null;
  /** Search context (tables, columns) gathered when search_context tool ran */
  search_context?: string | null;
  /** Agent-selected visualization; untyped historical results display as tables. */
  chart_type?: AnalyticsChartType;
  /** Chart title (e.g. "Revenue by Provider") */
  chart_title?: string;
  /** X-axis label (e.g. "Month") */
  x_label?: string;
  /** Y-axis label (e.g. "Revenue ($)") */
  y_label?: string;
  /** Column name to use for the x-axis/categories */
  x_column?: string;
  /** Column names to plot as series/values */
  y_columns?: string[];
  /** Columns that contain currency values (agent-specified, used for per-axis formatting) */
  currency_columns?: string[];
  /** For result_type="question": optional clickable options to refine the question */
  clarifying_options?: string[];
  /** Multiple artifacts when the agent ran multiple queries (grid display) */
  artifacts?: AnalyticsArtifact[];
}
